import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SignalIcon } from '@/components/ui/signal-icon';
import { BatteryLow, Wifi, CheckCircle2, SignalLow } from 'lucide-react';
import type { SystemHealthDevice } from '@/types/api';

interface SystemHealthSectionProps {
  title: string;
  icon: React.ReactNode;
  devices: SystemHealthDevice[];
  totalCount: number;
  viewAllHref: string;
  renderDetail: (device: SystemHealthDevice) => React.ReactNode;
}

function SystemHealthSection({ title, icon, devices, totalCount, viewAllHref, renderDetail }: SystemHealthSectionProps) {
  const navigate = useNavigate();

  if (devices.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</h4>
      </div>
      <div className="space-y-1">
        {devices.map((device) => (
          <button
            key={`${device.category}-${device.devEui}`}
            onClick={() => device.deviceId && navigate(`/devices/${device.deviceId}`)}
            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
          >
            <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
              {device.serialNumber}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
              {renderDetail(device)}
            </span>
          </button>
        ))}
        {totalCount > devices.length && (
          <button
            onClick={() => navigate(viewAllHref)}
            className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline mt-1 block"
          >
            View all {totalCount} &rarr;
          </button>
        )}
      </div>
    </div>
  );
}

export function SystemHealthCard({
  devices,
  lowBatteryCount,
  weakSignalCount,
  connectionIssuesCount,
}: {
  devices: SystemHealthDevice[];
  lowBatteryCount: number;
  weakSignalCount: number;
  connectionIssuesCount: number;
}) {
  const lowBattery = devices.filter(d => d.category === 'LowBattery');
  const weakSignal = devices.filter(d => d.category === 'WeakSignal');
  const connectionIssues = devices.filter(d => d.category === 'ConnectionIssues');

  const isEmpty = devices.length === 0;

  return (
    <Card variant="elevated">
      <CardHeader>
        <CardTitle>System Health</CardTitle>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-success-500" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">All devices healthy</p>
          </div>
        ) : (
          <div className="space-y-4">
            <SystemHealthSection
              title="Low Battery"
              icon={<BatteryLow className="w-4 h-4 text-warning-500" />}
              devices={lowBattery}
              totalCount={lowBatteryCount}
              viewAllHref="/alerts?type=LowBattery"
              renderDetail={(d) => <span>{d.detail}</span>}
            />
            <SystemHealthSection
              title="Weak Signal"
              icon={<SignalLow className="w-4 h-4 text-warning-500" />}
              devices={weakSignal}
              totalCount={weakSignalCount}
              viewAllHref="/alerts?type=LowSignal"
              renderDetail={(d) => <SignalIcon rssi={d.rssi ?? null} />}
            />
            <SystemHealthSection
              title="Connection Issues"
              icon={<Wifi className="w-4 h-4 text-warning-500" />}
              devices={connectionIssues}
              totalCount={connectionIssuesCount}
              viewAllHref="/devices?status=intermittent"
              renderDetail={(d) => <span>{d.detail}</span>}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
